/**
 * This class handles updating a WAI-ARIA region so that a screen reader will speak the provided text.
 */
var SpeechManager = /** @class */ (function () {
    /**
     * Construct a new instance of the SpeechManager class.
     * To use, pass an HTML element (most typically a div or span) with the following attributes set:
     * role="status"
     * aria-live="assertive"  (From initial testing, polite should also work here)
     * aria-atomic="true"
     * aria-relevant="additions text"
     *
     * The class implements a buffer to prevent a rapid succession of messages, intended for
     * an aria-live="assertive" region, from all getting announced. This makes the assertive region
     * behave closer to how they were intended, where the most recent message will truncate any
     * other queued up messages.
     *
     * Warning: Do not pass in an element with children that you want to keep. The manager will
     * delete all child elements as part of its normal operations.
     *
     * @param {HTMLElement} element - The element to use with the correct aria markup already in-place
     * @param {boolean} bBuffered - set this to true if you want buffered handling of your updates
     * @param {number} iWriteToDOMDelay - minimum time in milliseconds a DOM element must exist before it gets overwritten. Ignored if this instance is not buffered.
     * @param {number} iWriteBufferWait - how long in milliseconds the timer should wait since the last request before it writes to the DOM. Ignored if this instance is not buffered.
     */
    function SpeechManager(element, bBuffered, iWriteToDOMDelay, iWriteBufferWait) {
        if (bBuffered === void 0) { bBuffered = false; }
        if (iWriteToDOMDelay === void 0) { iWriteToDOMDelay = 500; }
        if (iWriteBufferWait === void 0) { iWriteBufferWait = 500; }
        this.speechElement = element;
        this.silentCharacter = "\u00A0";
        this.silentCharacterCount = 0;
        this.silentCharacterMaxCount = 5;
        this.bBuffered = bBuffered;
        if (bBuffered) {
            this.bufferTimerRunning = false; // Is a buffer timer running?
            this.bufferTimer = null; // The buffer timer
            this.lastWriteDOM = null; // Time stamp of when the last write to the DOM occurred
            this.writeBuffer = null; // Buffer to hold incoming write requests while a timer is running
            this._writeToDOMDelay = iWriteToDOMDelay;
            this._writeBufferWait = iWriteBufferWait;
        }
    }
    SpeechManager.prototype.addSilentText = function (text) {
        //  We append the silent text to ensure that we never try to say the exact same string twice (since VO is a big baby about that).
        var appendText = this.silentCharacterCount
            ? this.silentCharacter.repeat(this.silentCharacterCount)
            : "";
        this.silentCharacterCount = (this.silentCharacterCount + 1) % this.silentCharacterMaxCount;
        return text + appendText;
    };
    Object.defineProperty(SpeechManager.prototype, "buffered", {
        /**
         * Fetch the buffered or unbuffered state of the manager
         * @returns {boolean} True if the instance is buffered
         */
        get: function () {
            return this.bBuffered;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SpeechManager.prototype, "writeToDOMDelay", {
        /**
         * Fetch the time a DOM element must exist before it is overwritten.
         * Will be undefined if the instance is not buffered.
         *
         * @returns {number} Write to DOM delay time in milliseconds
         */
        get: function () {
            return this._writeToDOMDelay;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SpeechManager.prototype, "writeBufferWait", {
        /**
         * Fetch the interval the timer must wait since the last request before writing
         * to the DOM. Will be undefined if the instance is not buffered.
         *
         * @returns {number} Write buffer wait time in milliseconds
         */
        get: function () {
            return this._writeBufferWait;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Speak the provided text using the current buffering settings
     * @param {string} text - The text to speak
     */
    SpeechManager.prototype.notify = function (text) {
        var paddedText = this.addSilentText(text);
        if (this.bBuffered) {
            this.notifyBuffered(paddedText);
        }
        else {
            this.notifyUnbuffered(paddedText);
        }
    };
    ////////////////////////////////////////////////////////////////
    // The code in this section is specific to the buffered version
    ////////////////////////////////////////////////////////////////
    SpeechManager.prototype.notifyBuffered = function (text) {
        this.writeBufferedMessage(text);
    };
    // Stop any pending writes. Most useful when you are about to lose access to the window
    // object and want to prevent null pointer exceptions
    SpeechManager.prototype.stop = function () {
        if (this.bufferTimerRunning && this.bufferTimer) {
            window.clearTimeout(this.bufferTimer);
        }
    };
    /**
     * Process the new string to be spoken. Either add it directly to the DOM or if a rapid succession
     * of strings are received put it in a speech buffer so that only the latest in the rapid
     * succession gets announced.
     *
     * Browsers and screen readers will read every string added to a live region, even an "assertive"
     * region if there is not a triggering mechanism such as user keyboard input to indicate that the region
     * should behave like an assertive region. Without that triggering mechanism, the region in essence
     * acts like a "polite" region and all strings get appended to the screen reader's speech queue. The speech buffer
     * ensures only the last item in a string of successive write requests actually gets written to the DOM.
     * @param {string} message - The string to be spoken by the screen reader
     * @private
     */
    SpeechManager.prototype.writeBufferedMessage = function (message) {
        var _this = this;
        // if the last DOM write is greater than minimum time a DOM element must exist on the page
        // before it gets overwritten, and if a buffer timer is not running
        if (this.lastWriteDOM <= Date.now() - this.writeToDOMDelay && !this.bufferTimerRunning) {
            // write the message directly to the DOM
            this.writeBufferedMessageToDOM(message);
        }
        else {
            // write the message to the buffer
            // clear any old buffer timer
            window.clearTimeout(this.bufferTimer);
            // update the buffer to the last item received to be spoken by Speech Manager
            this.writeBuffer = message;
            // start a new buffer timer to write the last message received
            this.bufferTimer = window.setTimeout(function () {
                _this.writeBufferedMessageToDOM(_this.writeBuffer);
                _this.bufferTimerRunning = false;
            }, this.writeBufferWait);
            this.bufferTimerRunning = true;
        }
    };
    /**
     * Write the DOM element to the live region
     * @param {string} message - The string to be spoken by the screen reader
     * @memberof SpeechManager
     * @private
     */
    SpeechManager.prototype.writeBufferedMessageToDOM = function (message) {
        // remove all existing nodes
        // We do not need to wait before deleting nodes becasue 'assertive' means you want
        // this said immediately, no matter what
        while (this.speechElement.firstChild) {
            this.speechElement.removeChild(this.speechElement.firstChild);
        }
        //  Create a new div element containing the text to be spoken.
        var newElement = document.createElement("div");
        newElement.innerText = message;
        // add new node
        this.speechElement.appendChild(newElement);
        this.lastWriteDOM = Date.now(); // mark the time when the last node was written to the DOM
    };
    ////////////////////////////////////////////////////////////////
    // The code in this section is specific to the unbuffered version
    ////////////////////////////////////////////////////////////////
    SpeechManager.prototype.notifyUnbuffered = function (text) {
        var _this = this;
        // Create a new div element containing the text to be spoken.
        var newElement = document.createElement("div");
        newElement.innerText = text;
        // If there's no children already, just add the new element.
        // If there are children, set them to display=none, add the new element,
        // and then remove them after a delay (so Jaws will actually update).
        if (this.speechElement.childElementCount === 0) {
            this.speechElement.appendChild(newElement);
        }
        else {
            var childCount_1 = this.speechElement.children.length;
            for (var i = 0; i < childCount_1; i++) {
                if (this.speechElement.children[i] instanceof Element) {
                    this.speechElement.children[i].style.display = "none";
                }
            }
            this.speechElement.appendChild(newElement);
            // Add delay because Jaws is stupid.
            window.setTimeout(function () {
                // Delete children with style display = none
                childCount_1 = _this.speechElement.children.length;
                for (var i = 0; i < childCount_1; i++) {
                    if (_this.speechElement.children[i] instanceof Element && _this.speechElement.children[i].style.display === "none") {
                        _this.speechElement.children[i].remove();
                    }
                }
            }, 10);
        }
    };
    return SpeechManager;
}());
export { SpeechManager };
